import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Phone, ArrowRight } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";
import { Logo } from "@/components/Logo";

const FOOTER_COLS = [
  {
    title: "Services",
    links: [
      { to: "/services/mold-assessments", label: "Mold Assessments" },
      { to: "/services/indoor-air-quality", label: "Indoor Air Quality" },
      { to: "/services/moisture-mapping", label: "Moisture Mapping" },
      { to: "/services/water-damage-assessments", label: "Water Damage" },
      { to: "/services/remediation-protocols", label: "Remediation Protocols" },
      { to: "/services/post-remediation-verification", label: "Post-Remediation Verification" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/about", label: "About" },
      { to: "/industries", label: "Industries" },
      { to: "/locations", label: "Texas Locations" },
      { to: "/case-studies", label: "Case Studies" },
      { to: "/contact", label: "Contact" },
    ],
  },
  {
    title: "Learn",
    links: [
      { to: "/learning", label: "Learning Center" },
      { to: "/blog", label: "Blog" },
      { to: "/resources", label: "Resources" },
      { to: "/faq", label: "FAQ" },
      { to: "/schedule", label: "Schedule Assessment" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("Subscribed. Welcome to the Hurtler briefing.");
      setEmail("");
    } catch (err) {
      toast.error("Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#0F172A] text-white pt-20 pb-10">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 pb-16 border-b border-white/10">
          <div className="lg:col-span-2">
            <div className="mb-6">
              <Logo size="lg" showCaption />
            </div>
            <p className="text-white/70 text-sm max-w-md leading-relaxed">
              Independent environmental assessments, building science, and indoor air quality
              consulting — headquartered in South Texas.
            </p>
            <div className="mt-6 space-y-3 text-sm text-white/70">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#F59E0B]" />
                <span>204 E Cano Street, Edinburg TX 78539</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#F59E0B]" />
                <a href="tel:9562172604" className="hover:text-white">956.217.2604</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#F59E0B]" />
                <a href="mailto:maria@hurtler.com" className="hover:text-white">maria@hurtler.com</a>
              </div>
            </div>
          </div>

          {FOOTER_COLS.map((col) => (
            <div key={col.title}>
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold mb-5">
                {col.title}
              </div>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-sm text-white/70 hover:text-white"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 py-10 border-b border-white/10">
          <div className="max-w-md">
            <div className="font-display font-bold text-xl mb-1">The Hurtler Briefing</div>
            <p className="text-white/60 text-sm">
              Quarterly insight on environmental science, building diagnostics, and Texas regulations.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="flex w-full md:w-auto gap-3">
            <input
              data-testid="newsletter-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 md:w-72 px-5 py-3 rounded-full bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#7C3AED]"
            />
            <button
              data-testid="newsletter-submit-button"
              disabled={loading}
              type="submit"
              className="btn-primary"
            >
              {loading ? "..." : "Subscribe"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </form>
        </div>

        <div className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-white/50">
          <div>© {new Date().getFullYear()} Hurtler Environmental. All rights reserved.</div>
          <div className="flex items-center gap-2">
            <span>Independent Environmental Assessments</span>
            <span>•</span>
            <span>Building Science</span>
            <span>•</span>
            <span>Indoor Air Quality</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
